import { useCreateOnlineOrder } from './use-create-online-order';
import { useProductPicker } from './use-product-picker';
import { useNavigate } from 'react-router-dom';
import { captureException } from '@sentry/react';
import { useEffect, useMemo, useState } from 'react';
import { convertDeliveryModeStringToEnum } from '../component/on-off-button';

export default function useCreateOnlineOrderV2(props: {
  branchId: string;
  minimumPrice: number;
  openDialog: () => void;
}) {
  const { branchId, minimumPrice, openDialog } = props;

  const navigate = useNavigate();
  const [createOnlineOrder, { loading, error }] = useCreateOnlineOrder();

  const pickedProducts = useProductPicker((state) => state.products);
  const clearCart = useProductPicker((state) => state.clear);

  const [deliveryMode, setDeliveryMode] = useState(() => localStorage.getItem('deliveryMode')?.toLowerCase());

  // Listen to localStorage changes (across tabs)
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'deliveryMode') {
        setDeliveryMode(e.newValue?.toLowerCase() ?? undefined);
      }
    };
    window.addEventListener('storage', handleStorageChange);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  // Also listen to localStorage changes in the same tab
  useEffect(() => {
    const interval = setInterval(() => {
      const current = localStorage.getItem('deliveryMode')?.toLowerCase();
      setDeliveryMode((prev) => (prev !== current ? current : prev));
    }, 500);
    return () => {
      clearInterval(interval);
    };
  }, []);

  const cartTotalPrice = useMemo(() => {
    return pickedProducts.reduce((total, product) => {
      const productPrice = product.configurations.reduce((configTotal, config) => {
        return configTotal + (config.price || 0);
      }, 0);
      return total + productPrice * product.quantity;
    }, 0);
  }, [pickedProducts]);

  const selectedDeliveryMode = useMemo(() => convertDeliveryModeStringToEnum(deliveryMode), [deliveryMode]);

  // eslint-disable-next-line @typescript-eslint/no-unsafe-enum-comparison
  const isCartBelowMinimum = selectedDeliveryMode == 'DELIVERY' && cartTotalPrice < minimumPrice;

  const createOrderHandler = () => {
    createOnlineOrder({
      variables: {
        branchID: branchId,
        input: {
          initialDeliveryMode: convertDeliveryModeStringToEnum(localStorage.getItem('deliveryMode')?.toLowerCase()),
          customerLanguage: localStorage.getItem('language'),
          pickedProducts: pickedProducts.map((product) => ({
            productID: product.productID,
            quantity: product.quantity,
            configurations: product.configurations.map((config) => ({
              configurationID: config.configurationID,
              valueID: config.valueID,
            })),
          })),
        },
      },
    })
      .then((onlineOrder) => {
        if (!onlineOrder.data?.storefront_createOnlineOrder) return;
        clearCart();
        navigate(`/online-order/${onlineOrder.data.storefront_createOnlineOrder.id}`);
      })
      .catch((error) => {
        captureException(error);
      });
  };

  return {
    createOnlineOrder: isCartBelowMinimum ? openDialog : createOrderHandler,
    creatingOnlineOrder: loading,
    creatingError: error,
  };
}
